/**
 * PurchaseOrderDetailDialog — read-only PO detail with lifecycle
 * actions (Submit / Approve / Reject / Cancel / Short-Close / Download
 * PDF / Edit). Edit is only surfaced for DRAFT POs (delegates to
 * the create dialog with `initialPo`). Other status-specific buttons
 * follow the Phase 1 backend rules exactly.
 *
 * Phase 2 (Feb 2026) — adds the inward-gate check summary strip
 * (non-CLEAN `po_exceptions`) and the stock-allocation panel
 * (`po_allocations`), plus live `open_qty` on every line row. All
 * three feeds are refetched on every dialog open so the panel
 * never renders stale cached numbers from the list-screen row.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Download, Loader2, Pencil, CheckCircle, XCircle, Ban,
  MinusCircle, Send, ShieldAlert, PackageCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Sep 2026 — `billing_location` and `shipping_location` come back
// from `_expand_po_locations` as either a plain string (unmatched
// free-text) or a structured dict — `{name, address}` for a branch
// or `{kind, name, address, gstin}` for a job-work vendor. Rendering
// the dict directly into JSX crashes with React error #31 ("Objects
// are not valid as a React child"). This helper is the ONLY entry
// point that render sites should use for these two fields; it never
// mutates the underlying value (write path stays a plain string via
// the create dialog), it just formats for display.
const fmtLoc = (v) => {
  if (v == null || v === '') return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    const name = v.name || '';
    const address = v.address || '';
    if (name && address) return `${name} — ${address}`;
    return name || address || '—';
  }
  return String(v);
};

const STATUS_STYLES = {
  DRAFT: 'bg-gray-100 text-gray-800',
  PENDING_APPROVAL: 'bg-yellow-100 text-yellow-800',
  OPEN: 'bg-blue-100 text-blue-800',
  PARTIAL: 'bg-indigo-100 text-indigo-800',
  CLOSED: 'bg-green-100 text-green-800',
  SHORT_CLOSED: 'bg-orange-100 text-orange-800',
  CANCELLED: 'bg-zinc-200 text-zinc-700',
  REJECTED: 'bg-red-100 text-red-800',
};

// Phase-2 chip palettes ─────────────────────────────────────────────
const BILL_KIND_CHIP = {
  RM_INWARD: 'bg-sky-100 text-sky-800 border-sky-200',
  RM_INWARD_NO_INVOICE: 'bg-teal-100 text-teal-800 border-teal-200',
  FG_INWARD: 'bg-violet-100 text-violet-800 border-violet-200',
  JOBWORK_INVOICE: 'bg-amber-100 text-amber-800 border-amber-200',
};
const BILL_KIND_LABEL = {
  RM_INWARD: 'RM Inward',
  RM_INWARD_NO_INVOICE: 'No-Invoice',
  FG_INWARD: 'FG Inward',
  JOBWORK_INVOICE: 'Jobwork',
};
const RESULT_CHIP = {
  WARN: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  BLOCK: 'bg-red-100 text-red-800 border-red-200',
  ERROR: 'bg-orange-100 text-orange-800 border-orange-200',
};
const CHECK_CODE_CHIP = {
  NO_PO: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  OVER_QTY: 'bg-red-50 text-red-700 border-red-200',
  PRICE_VAR: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  UOM_MISMATCH: 'bg-orange-50 text-orange-700 border-orange-200',
  BRANCH_MISMATCH: 'bg-blue-50 text-blue-700 border-blue-200',
  PO_NOT_OPEN: 'bg-purple-50 text-purple-700 border-purple-200',
};

function fmtMoney(v) {
  const n = parseFloat(v || 0);
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtQty(v, uom) {
  if (v === null || v === undefined) return '—';
  const n = parseFloat(v);
  if (Number.isNaN(n)) return '—';
  return `${n.toLocaleString('en-IN', { maximumFractionDigits: 4 })}${uom ? ` ${uom}` : ''}`;
}

function fmtDate(v) {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return v; }
}

function fmtDateTime(v) {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return v; }
}

function lineOpenQty(l) {
  const ordered = parseFloat(l?.quantity_ordered || 0);
  const received = parseFloat(l?.quantity_received || 0);
  const open = ordered - received;
  return open < 0 ? 0 : open;
}

export default function PurchaseOrderDetailDialog({ open, po, onClose, onChange, onEdit }) {
  const [busy, setBusy] = useState('');
  const [reason, setReason] = useState('');
  const [reasonAction, setReasonAction] = useState(null); // 'reject' | 'short-close' | null

  // Phase-2 panel state — refetched on every dialog open so the
  // allocations, exceptions, and line quantities are never stale.
  const [livePO, setLivePO] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [panelLoading, setPanelLoading] = useState(false);
  const [panelError, setPanelError] = useState(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const authHeaders = { Authorization: `Bearer ${token}` };

  const refetchPanels = useCallback(async () => {
    if (!po?.id) return;
    setPanelLoading(true);
    setPanelError(null);
    try {
      const [poResp, allocResp, excResp] = await Promise.all([
        axios.get(`${API}/api/purchase-orders/${po.id}`, { headers: authHeaders }),
        axios.get(`${API}/api/purchase-orders/${po.id}/allocations`, { headers: authHeaders }),
        axios.get(`${API}/api/purchase-orders/${po.id}/exceptions`, { headers: authHeaders }),
      ]);
      setLivePO(poResp.data || null);
      setAllocations(Array.isArray(allocResp.data) ? allocResp.data : []);
      setExceptions(Array.isArray(excResp.data) ? excResp.data : []);
    } catch (err) {
      // Feb 2026 (BUG-PO-RAW-DETAIL-CRASH) — see identical guard
      // in `PurchaseOrderCreateDialog.jsx`. `setPanelError` renders
      // its value in JSX, so a raw `detail` object crashes the
      // panel. `formatApiError` gives us a string every time.
      setPanelError(formatApiError(err, 'Failed to load PO detail').message);
    } finally {
      setPanelLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [po?.id]);

  useEffect(() => {
    if (open && po?.id) {
      refetchPanels();
    } else if (!open) {
      // Clear panel state on close so a re-open never briefly shows
      // the previous PO's numbers.
      setLivePO(null);
      setAllocations([]);
      setExceptions([]);
      setPanelError(null);
      setPanelLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, po?.id]);

  // Prefer live PO for line render + status; fall back to prop
  // during the initial fetch so the dialog isn't blank.
  const effectivePO = livePO || po;

  // Non-CLEAN exceptions only (contract: hide the strip when
  // everything is clean).
  const nonCleanExceptions = useMemo(
    () => (exceptions || []).filter(
      (e) => (e?.enforcement_result || 'CLEAN').toUpperCase() !== 'CLEAN',
    ),
    [exceptions],
  );

  // Cross-reference allocations with PO lines (for variance %).
  const poLineByIdRef = useMemo(() => {
    const map = {};
    (effectivePO?.lines || []).forEach((l) => {
      if (l?.id) map[l.id] = l;
    });
    return map;
  }, [effectivePO?.lines]);

  const variancePct = (alloc) => {
    const poLine = poLineByIdRef[alloc?.po_line_id];
    const poRate = parseFloat(poLine?.unit_price || 0);
    const billRate = parseFloat(alloc?.unit_price || 0);
    if (!poRate || !billRate) return null;
    return ((billRate - poRate) / poRate) * 100;
  };

  if (!effectivePO) return null;

  const runAction = async (path, body = null) => {
    setBusy(path);
    try {
      const res = await axios.post(`${API}/api/purchase-orders/${effectivePO.id}/${path}`, body || {}, { headers });
      toast.success(`PO ${path.replace('-', ' ')} succeeded`);
      onChange?.(res.data);
      setReason('');
      setReasonAction(null);
      // Refresh panels after any lifecycle action.
      refetchPanels();
    } catch (err) {
      // Feb 2026 (BUG-PO-RAW-DETAIL-CRASH) — action errors carry
      // structured details too (e.g. RBAC 403 or state-transition
      // 409). Same treatment.
      toast.error(formatApiError(err, 'Action failed').message);
    }
    setBusy('');
  };

  const submitReason = async () => {
    if (!reason.trim()) return toast.error('Reason is required');
    await runAction(reasonAction, { reason: reason.trim() });
  };

  const downloadPDF = async () => {
    setBusy('pdf');
    try {
      const res = await axios.get(`${API}/api/purchase-orders/${effectivePO.id}/pdf`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(effectivePO.po_number || 'PO').replace(/\//g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Feb 2026 (BUG-PO-RAW-DETAIL-CRASH, defence-in-depth) —
      // PDF download historically used a bare string here, but
      // routing through `formatApiError` too means no PO error
      // path in this file can regress into rendering a raw
      // object. The AST guard in
      // `__tests__/poRawErrorDetailRegression.test.js` enforces
      // this file-wide.
      toast.error(formatApiError(err, 'PDF download failed').message);
    }
    setBusy('');
  };

  const status = effectivePO.status || 'DRAFT';
  const canEdit = status === 'DRAFT';
  const canSubmit = status === 'DRAFT';
  const canApproveReject = status === 'PENDING_APPROVAL';
  const canCancel = ['OPEN', 'PARTIAL', 'PENDING_APPROVAL'].includes(status);
  const canShortClose = ['OPEN', 'PARTIAL'].includes(status);

  const totals = effectivePO.totals || {};

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="po-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>Purchase Order — {effectivePO.po_number}</span>
            <Badge className={STATUS_STYLES[status] || 'bg-gray-100'} data-testid="po-status-badge">{status}</Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Header grid */}
        <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
          <div><span className="text-zinc-500">Vendor:</span> <div className="font-medium">{effectivePO.vendor_name}</div></div>
          <div><span className="text-zinc-500">Branch:</span> <div className="font-medium">{effectivePO.branch}</div></div>
          <div><span className="text-zinc-500">PO Type:</span> <div className="font-medium">{effectivePO.po_type}</div></div>
          <div><span className="text-zinc-500">Order Date:</span> <div>{fmtDate(effectivePO.order_date)}</div></div>
          <div><span className="text-zinc-500">Expected Delivery:</span> <div>{fmtDate(effectivePO.expected_delivery_date)}</div></div>
          <div><span className="text-zinc-500">Payment Terms:</span> <div>{effectivePO.payment_terms || '—'}</div></div>
          <div><span className="text-zinc-500">Billing:</span> <div>{fmtLoc(effectivePO.billing_location)}</div></div>
          <div><span className="text-zinc-500">Shipping:</span> <div>{fmtLoc(effectivePO.shipping_location)}</div></div>
          <div><span className="text-zinc-500">Source:</span> <div>{effectivePO.source?.kind || 'MANUAL'}</div></div>
        </div>

        {/* Approval flags */}
        {effectivePO.approval_flags?.length > 0 && (
          <div className="mt-4 border rounded-md p-3 bg-amber-50" data-testid="po-flags">
            <div className="text-sm font-semibold text-amber-900 mb-1">Approval flags</div>
            {effectivePO.approval_flags.map((f, i) => (
              <div key={i} className="text-sm text-amber-900">
                <Badge variant="outline" className="mr-2">{f.code}</Badge>
                {f.message}
              </div>
            ))}
          </div>
        )}

        {/* Lines — with live open_qty (contract AC2) */}
        <div className="mt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">#</TableHead>
                <TableHead>Item &amp; Description</TableHead>
                <TableHead className="w-[90px]">HSN/SAC</TableHead>
                <TableHead className="w-[110px] text-right">Ordered</TableHead>
                <TableHead className="w-[110px] text-right">Received</TableHead>
                <TableHead className="w-[110px] text-right">Open</TableHead>
                <TableHead className="w-[100px] text-right">Rate</TableHead>
                <TableHead className="w-[120px] text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(effectivePO.lines || []).map((l, i) => {
                const open = lineOpenQty(l);
                return (
                  <TableRow key={l.id || i}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>
                      <div className="font-medium">{l.item_id}</div>
                      <div className="text-xs text-zinc-500">{l.description}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{l.hsn_or_sac || '—'}</TableCell>
                    <TableCell className="text-right font-mono">{fmtQty(l.quantity_ordered, l.unit_of_measure)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtQty(l.quantity_received, l.unit_of_measure)}</TableCell>
                    <TableCell
                      className={`text-right font-mono ${open === 0 ? 'text-zinc-400' : 'text-emerald-700 font-semibold'}`}
                      data-testid={`po-line-open-qty-${i}`}
                    >
                      {fmtQty(open, l.unit_of_measure)}
                    </TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(l.unit_price)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(l.line_total)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Totals */}
        <div className="mt-4 flex justify-end">
          <div className="w-80 space-y-1 text-sm">
            <div className="flex justify-between"><span>Sub Total</span><span className="font-mono">{fmtMoney(totals.subtotal)}</span></div>
            <div className="flex justify-between"><span>{effectivePO.tax_type || (effectivePO.tax_rate ? 'IGST' : 'Tax')} ({effectivePO.tax_rate || 0}%)</span><span className="font-mono">{fmtMoney(totals.tax_amount)}</span></div>
            <div className="flex justify-between"><span>Amount Withheld ({effectivePO.withholding_rate || 0}%)</span><span className="font-mono text-red-600">-{fmtMoney(totals.withholding_amount)}</span></div>
            <div className="flex justify-between"><span>Adjustment</span><span className="font-mono">{fmtMoney(totals.adjustment_amount)}</span></div>
            <div className="flex justify-between text-base font-bold border-t pt-1"><span>Total</span><span className="font-mono" data-testid="po-total-grand">₹ {fmtMoney(totals.grand_total)}</span></div>
          </div>
        </div>

        {effectivePO.notes && (
          <div className="mt-4 text-sm">
            <div className="font-semibold">Notes</div>
            <div className="text-zinc-600 whitespace-pre-wrap">{effectivePO.notes}</div>
          </div>
        )}

        {/* ── Inward-Gate Checks strip (contract AC3) ────────────
            Hidden entirely when every resolved bill is CLEAN. */}
        {nonCleanExceptions.length > 0 && (
          <div
            className="mt-6 border rounded-md p-3 bg-red-50/40"
            data-testid="po-exceptions-panel"
          >
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert className="h-4 w-4 text-red-700" />
              <div className="text-sm font-semibold text-red-900">
                Inward-Gate Checks ({nonCleanExceptions.length})
              </div>
            </div>
            <div className="space-y-2">
              {nonCleanExceptions.map((exc, i) => {
                const result = (exc.enforcement_result || '').toUpperCase();
                const overrideCleared = !!exc?.override?.resolved_at;
                const nonCleanLines = (exc.per_line || []).filter(
                  (pl) => (pl?.check_code || 'CLEAN').toUpperCase() !== 'CLEAN',
                );
                return (
                  <div
                    key={`${exc.bill_kind}:${exc.bill_ref}`}
                    className="rounded border bg-white p-2"
                    data-testid={`po-exception-row-${i}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={BILL_KIND_CHIP[exc.bill_kind] || 'bg-zinc-100'} variant="outline">
                        {BILL_KIND_LABEL[exc.bill_kind] || exc.bill_kind}
                      </Badge>
                      <span className="font-mono text-xs">{exc.bill_ref}</span>
                      <Badge className={RESULT_CHIP[result] || 'bg-zinc-100'} variant="outline">
                        {result}
                      </Badge>
                      {overrideCleared && (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" variant="outline">
                          override cleared
                        </Badge>
                      )}
                    </div>
                    {nonCleanLines.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {nonCleanLines.map((pl, j) => (
                          <Badge
                            key={j}
                            className={CHECK_CODE_CHIP[pl.check_code] || 'bg-zinc-100'}
                            variant="outline"
                            title={pl.detail || ''}
                            data-testid={`po-check-badge-${i}-${j}`}
                          >
                            {pl.check_code}{pl.line_item_id ? ` · ${pl.line_item_id}` : ''}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Inward Allocations panel (contract AC1, AC4, AC5) ─── */}
        <div
          className="mt-6 border rounded-md p-3"
          data-testid="po-allocations-panel"
        >
          <div className="flex items-center gap-2 mb-2">
            <PackageCheck className="h-4 w-4 text-zinc-600" />
            <div className="text-sm font-semibold text-zinc-800">
              Inward Allocations ({allocations.length})
            </div>
          </div>
          {panelLoading && !allocations.length ? (
            <div className="text-xs text-zinc-500 flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading…
            </div>
          ) : panelError ? (
            <div className="text-xs text-red-700" data-testid="po-allocations-error">
              {panelError}
            </div>
          ) : allocations.length === 0 ? (
            <div
              className="text-xs text-zinc-500 italic"
              data-testid="po-allocations-empty"
            >
              No inward activity yet — bills that resolve against this PO will appear here.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Bill Kind</TableHead>
                  <TableHead>Bill Ref</TableHead>
                  <TableHead className="w-[60px] text-right">Line #</TableHead>
                  <TableHead className="w-[120px] text-right">Qty</TableHead>
                  <TableHead className="w-[110px] text-right">Unit Price</TableHead>
                  <TableHead className="w-[90px] text-right">Variance</TableHead>
                  <TableHead className="w-[160px]">Matched At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allocations.map((alloc, i) => {
                  const v = variancePct(alloc);
                  const vClass =
                    v === null ? 'text-zinc-400' :
                    Math.abs(v) < 0.5 ? 'text-zinc-500' :
                    v > 0 ? 'text-red-600' : 'text-emerald-700';
                  return (
                    <TableRow key={alloc.id || i} data-testid={`po-allocations-row-${i}`}>
                      <TableCell>
                        <Badge
                          className={BILL_KIND_CHIP[alloc.bill_kind] || 'bg-zinc-100'}
                          variant="outline"
                        >
                          {BILL_KIND_LABEL[alloc.bill_kind] || alloc.bill_kind}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{alloc.bill_ref}</TableCell>
                      <TableCell className="text-right font-mono">
                        {alloc.line_index !== undefined && alloc.line_index !== null ? alloc.line_index + 1 : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {fmtQty(alloc.quantity, alloc.unit_of_measure)}
                      </TableCell>
                      <TableCell className="text-right font-mono">{fmtMoney(alloc.unit_price)}</TableCell>
                      <TableCell className={`text-right font-mono ${vClass}`}>
                        {v === null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`}
                      </TableCell>
                      <TableCell className="text-xs">
                        <span title={new Date(alloc.matched_at).toISOString()}>
                          {fmtDateTime(alloc.matched_at)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Reason capture — appears when Reject/Short-close clicked */}
        {reasonAction && (
          <div className="mt-4 border rounded-md p-3 bg-zinc-50" data-testid="po-reason-panel">
            <Label>{reasonAction === 'reject' ? 'Reject reason' : 'Short-close reason'}</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="po-reason-input" />
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={() => { setReasonAction(null); setReason(''); }}>Cancel</Button>
              <Button size="sm" onClick={submitReason} disabled={!!busy} data-testid="po-reason-submit">
                {busy === reasonAction ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Confirm
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4 flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={downloadPDF} disabled={!!busy} data-testid="po-pdf-btn">
            {busy === 'pdf' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Download PDF
          </Button>
          {canEdit && (
            <Button variant="outline" onClick={() => onEdit?.(effectivePO)} data-testid="po-edit-btn">
              <Pencil className="h-4 w-4 mr-2" />Edit
            </Button>
          )}
          {canSubmit && (
            <Button onClick={() => runAction('submit')} disabled={!!busy} data-testid="po-submit-btn">
              <Send className="h-4 w-4 mr-2" />Submit
            </Button>
          )}
          {canApproveReject && (
            <>
              <Button onClick={() => runAction('approve')} disabled={!!busy} data-testid="po-approve-btn" className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-2" />Approve
              </Button>
              <Button variant="destructive" onClick={() => setReasonAction('reject')} disabled={!!busy} data-testid="po-reject-btn">
                <XCircle className="h-4 w-4 mr-2" />Reject
              </Button>
            </>
          )}
          {canCancel && (
            <Button variant="outline" onClick={() => runAction('cancel')} disabled={!!busy} data-testid="po-cancel-btn">
              <Ban className="h-4 w-4 mr-2" />Cancel PO
            </Button>
          )}
          {canShortClose && (
            <Button variant="outline" onClick={() => setReasonAction('short-close')} disabled={!!busy} data-testid="po-short-close-btn">
              <MinusCircle className="h-4 w-4 mr-2" />Short-Close
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={!!busy}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
